import Dropzone from 'react-dropzone';
import ColorThief from 'colorthief';
import Spinner from '../components/ui/Spinner';

import IndexLayout from '../layouts';
import Wrapper from '../components/Wrapper';
import SiteNav from '../components/header/SiteNav';

import { SiteHeader, outer, inner, SiteMain } from '../styles/shared';
import * as React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { PostFullHeader, PostFullTitle, NoImage, PostFull } from '../templates/post';
import { PostFullContent } from '../components/PostContent';
import Footer from '../components/Footer';
import Helmet from 'react-helmet';

//import ShareButtons from '../components/ShareButtons';
import MoreGoodies from '../components/more-goodies/MoreGoodies';
import BackToTop from '../components/BackToTop';

import { colors } from '../styles/colors';

const colorThief = new ColorThief();

// https://lokeshdhakar.com/projects/color-thief/#support
const rgbToHex = (r, g, b) =>
  // tslint:disable-next-line:prefer-template
  '#' +
  [r, g, b]
    .map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    })
    .join('')
    .toUpperCase();

const PageTemplate = css`
.site-main {
background #fff;
padding-bottom: 4vw;
}

.post-content {
max-width: 1000px;
margin: -2.5rem auto 0;
}
.post-full-content {
  background: transparent;
}
p {
  font-size: 2rem !important;

}
h2 {
  margin-top: 0;
  font-size: 3rem;

  small {
    font-size: 1.5rem;
    font-weight: normal;
  }
}
h2::before {
all: unset !important;
}
h3 {
text-decoration: none !important;
padding-top: 5rem;
text-align: center;
}
`;

const FactsBox = styled.div`
  margin-top: 1.5rem;
  background: #EFFAF2;
  padding: 1.3rem 1.3rem 0.45rem;

  @media (max-width: 750px) {
    padding: 0.1rem 0.1rem 0.1rem;
  }

  li:first-letter {
    color: #ACE3BA;
  }

  h3 {
    padding-top: 0;
  }
`;

const DropZoneDiv = styled.div`
cursor: pointer;
border: 2px dashed  ${colors.beta};
background: repeating-linear-gradient(
  45deg,
  transparent,
  transparent 35px,
  ${colors.beta} 0,
  ${colors.beta} 70px
);
  max-width: 600px;
  padding: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;

  p {
    margin-bottom: 0;
    font-weight: bold;
  }
`;

const ResultImg = styled.img`
  max-height: 800px;
  object-fit: cover;

  :active {
    filter: none;
  }
`;

interface SwatchProps {
  resultColor: number[];
}

const SwatchWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

interface ActiveWrapperProps {
  activeColor: number[];
}

const ActiveWrapper = styled.ul<ActiveWrapperProps>`
  display: flex;
  justify-content: space-between;
  border-bottom: 10px solid
    ${props => `rgb(${props.activeColor[0]},${props.activeColor[1]},${props.activeColor[2]})`};
  margin: 0 0 2rem;
  padding: 0 !important;
  li {
    margin: 0;
    list-style-type: none;
  }
  li span {
    color: #D7F2DE;
  }
`;

const Swatch = styled.div<SwatchProps>`
  background: ${props =>
    `rgb(${props.resultColor[0]},${props.resultColor[1]},${props.resultColor[2]})`};
  width: 100px;
  height: 80px;
  flex-grow: 1;
  cursor: pointer;

  transition: transform 0.25s ease-out;

  :hover {
    transform: scale(1.2);
    z-index: 1;
  }

  @media (max-width: 600px) {
    height: 40px;
  }
`;

interface PhotoColorExtractorState {
  hasCustomImage: boolean;
  photoData: any;
  error: boolean;
  palette: any[];
  active: number;
  // dominantColor: number[];
}

class PhotoColorExtractor extends React.Component<undefined, PhotoColorExtractorState> {
  state = {
    error: false,
    hasCustomImage: false,
    photoData: undefined,
    // dominantColor: [],
    palette: [],
    active: 0,
  };

  exampleUrl = '/guide/images/color-extractor/example/color-extractor-example.jpg';
  photoRef;

  constructor(props) {
    super(props);
    this.photoRef = React.createRef();
  }

  onDrop = file => {
    const reader = new FileReader();

    reader.onload = () => {
      this.setState({ photoData: reader.result });
      this.vibrant();
    };

    reader.readAsDataURL(file[0]);
  };

  vibrant = () => {
    this.setState({ palette: [], active: 0 });
    setTimeout(() => {
      // const dominantColor = colorThief.getColor(this.photoRef.current);
      // this.setState({ dominantColor });

      const palette = colorThief.getPalette(this.photoRef.current, 7);
      this.setState({ palette });
    }, 1000);
  };

  componentDidMount() {
    this.vibrant();
  }

  render() {
    return (
      <IndexLayout>
        <Helmet>
          <title>Photo Color Extractor | Giggster Guide</title>
          <meta
            name="description"
            content="A simple online tool that lets you upload an image and get a color palette with the main colors found in the image. Useful for color grading/color toning."
          />

          <meta property="og:site_name" content="Giggster Guide" />
          <meta property="og:type" content="article" />
          <meta property="og:title" content="Photo Color Extractor" />
          <meta
            property="og:description"
            content="A simple online tool that lets you upload an image and get a color palette with the main colors found in the image. Useful for color grading/color toning."
          />
          <meta property="og:url" content="https://giggster.com/guide/color-extractor/" />
          <meta property="og:image" content="https://giggster.com/guide/covers/color-extractor.jpg" />
          <meta property="article:publisher" content="https://www.facebook.com/giggsterHQ" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Photo Color Extractor" />
          <meta
            name="twitter:description"
            content="A simple online tool that lets you upload an image and get a color palette with the main colors found in the image. Useful for color grading/color toning."
          />
          <meta name="twitter:url" content="https://giggster.com/guide/color-extractor/" />
          <meta name="twitter:image" content="https://giggster.com/guide/covers/color-extractor.jpg" />
          <meta name="twitter:site" content="@Giggster" />
          <meta name="twitter:creator" content="@Giggster" />
        </Helmet>
        <Wrapper css={PageTemplate}>
          <header css={[outer, SiteHeader]}>
            <div css={inner}>
              <SiteNav />
            </div>
          </header>
          <main id="site-main" className="site-main" css={[SiteMain, outer]}>
            <article className="post page" css={[PostFull, NoImage]}>
              <PostFullHeader>
                <PostFullTitle>Photo Color Extractor</PostFullTitle>
                <p className="t2">Extract a color palette of the dominant colors in an image</p>
                {/*<ShareButtons
                  title={'Photo Color Extractor'}
                  slug={'/color-extractor/'}
                  imgSrc={'covers/color-extractor.jpg'}
                />*/}
              </PostFullHeader>

              <PostFullContent className="post-full-content">
                <div className="post-content text-center">
                  <h2 className="text-center">Upload Your Image</h2>

                  <Dropzone multiple={false} accept="image/jpeg, image/png" onDrop={this.onDrop}>
                    {({ getRootProps, getInputProps }) => (
                      <section>
                        <DropZoneDiv {...getRootProps()}>
                          <input {...getInputProps()} />
                          <p>Drag a photo here, or click to select one</p>
                        </DropZoneDiv>
                      </section>
                    )}
                  </Dropzone>

                  <hr />

                  {this.state.active ? (
                    <ActiveWrapper activeColor={this.state.palette[this.state.active - 1]}>
                      <li>
                        Red: <span>{this.state.palette[this.state.active - 1][0]}</span>
                      </li>
                      <li>
                        Green: <span>{this.state.palette[this.state.active - 1][1]}</span>
                      </li>
                      <li>
                        Blue: <span>{this.state.palette[this.state.active - 1][2]}</span>
                      </li>
                      <li>
                        Hex:{' '}
                        <span>
                          {rgbToHex(
                            this.state.palette[this.state.active - 1][0],
                            this.state.palette[this.state.active - 1][1],
                            this.state.palette[this.state.active - 1][2],
                          )}
                        </span>
                      </li>
                    </ActiveWrapper>
                  ) : (
                    ''
                  )}

                  <SwatchWrapper>
                    {this.state.palette.length > 0 &&
                      this.state.palette.map((color, idx) => (
                        <Swatch
                          key={idx}
                          resultColor={color}
                          // tslint:disable-next-line:react-this-binding-issue
                          onClick={() => this.setState({ active: idx + 1 })}
                        />
                      ))}
                    {this.state.palette.length === 0 && <Spinner />}
                  </SwatchWrapper>

                  {this.state.photoData ? (
                    <ResultImg
                      src={this.state.photoData}
                      // tslint:disable-next-line
                      onTouchStart={() => {}}
                      ref={this.photoRef}
                    />
                  ) : (
                    <ResultImg
                      src={this.exampleUrl}
                      ref={this.photoRef}
                      // tslint:disable-next-line
                      onTouchStart={() => {}}
                    />
                  )}

                  <hr />

                  <FactsBox>
                    <h3>How it works</h3>
                    <ul>
                      <li>
                        &#9673; Just upload a photo and you'll get a palette of the 7 main/dominant
                        colors in the photograph. Click/tap on any of the 7 color swatches to get
                        the RGB (red, green, blue) values or the hex value for that color.
                      </li>
                    </ul>
                  </FactsBox>
                  {/* <hr /> */}
                </div>
              </PostFullContent>
            </article>
            <MoreGoodies slug="color-extractor" />
          </main>
          <BackToTop />
          <Footer />
        </Wrapper>
      </IndexLayout>
    );
  }
}

export default PhotoColorExtractor;
